export interface Question {
    id: string;
    text: string;
    dependsOn?: string;
    showIf?: string;
}

export interface CAROClauseConfig {
    id: string;
    title: string;
    questions: Question[];
    forceYes?: boolean;
}

export const CARO_CLAUSES: Record<string, CAROClauseConfig> = {
    "i": {
        id: "i",
        title: "(i) Property, Plant & Equipment and Intangible Assets",
        questions: [
            { id: "q1", text: "Is the company maintaining proper records showing full particulars, including quantitative details and situation of PPE?" },
            { id: "q2", text: "Is the company maintaining proper records showing full particulars of intangible assets?" },
            { id: "q3", text: "Has the management physically verified PPE at reasonable intervals?" },
            { id: "q4", text: "Were material discrepancies noticed during physical verification?", dependsOn: "q3", showIf: "yes" },
            { id: "q5", text: "If discrepancies were noticed, were they properly dealt with in the books of account?", dependsOn: "q4", showIf: "yes" },
            { id: "q6", text: "Are all assets physically verified during the year?", dependsOn: "q3", showIf: "yes" },
            { id: "q7", text: "Are the assets physically verified at a point in time?", dependsOn: "q3", showIf: "yes" },
            { id: "q8", text: "Has the company disclosed immovable properties in its financial statements?" },
            { id: "q9", text: "Are the title deeds of all immovable properties disclosed in the financial statements held in the company's name?", dependsOn: "q8", showIf: "yes" },
            { id: "q10", text: "Does the company have leased immovable properties?", dependsOn: "q8", showIf: "yes" },
            { id: "q11", text: "Are lease agreements duly executed in favour of the company?", dependsOn: "q10", showIf: "yes" },
            { id: "q12", text: "Are any title deeds under legal dispute?", dependsOn: "q8", showIf: "yes" },
            { id: "q13", text: "Has the company revalued its PPE or intangible assets during the year?" },
            { id: "q14", text: "If revalued, was the revaluation done by a Registered Valuer?", dependsOn: "q13", showIf: "yes" },
            { id: "q15", text: "Did the revaluation result in a change of 10% or more in the aggregate net carrying value of each class of PPE or intangible assets?", dependsOn: "q13", showIf: "yes" },
            { id: "q16", text: "Are there any proceedings initiated or pending against the company for holding benami property under the Benami Transactions (Prohibition) Act?" },
            { id: "q17", text: "If yes, has the company appropriately disclosed the details in its financial statements?", dependsOn: "q16", showIf: "yes" },
            { id: "q18", text: "Have any proceedings been disposed off during the financial year/subsequent to the balance sheet date?", dependsOn: "q16", showIf: "yes" },
            { id: "q19", text: "Have any proceedings been initiated subsequent to the balance sheet date?", dependsOn: "q16", showIf: "yes" }
        ]
    },
    "ii": {
        id: "ii",
        title: "(ii) Inventory",
        questions: [
            { id: "q1", text: "Does the company have inventory?" },
            { id: "q2", text: "Has the management conducted physical verification of inventory at reasonable intervals?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Was the coverage and procedure of such verification appropriate?", dependsOn: "q2", showIf: "yes" },
            { id: "q4", text: "Were discrepancies of 10% or more noticed in any class of inventory?", dependsOn: "q2", showIf: "yes" },
            { id: "q5", text: "If discrepancies were noticed, were they properly dealt with in the books of account?", dependsOn: "q4", showIf: "yes" },
            { id: "q6", text: "Has the company been sanctioned working capital limits?" },
            { id: "q7", text: "Are the limits sanctioned on the basis of security of current assets?", dependsOn: "q6", showIf: "yes" },
            { id: "q8", text: "Are the limits sanctioned in excess of Rs. 5 crores from banks or financial institutions?", dependsOn: "q6", showIf: "yes" },
            { id: "q9", text: "Are the quarterly returns/statements filed with banks/financial institutions in agreement with the books of account?", dependsOn: "q8", showIf: "yes" },
            { id: "q10", text: "Are the discrepancies significant?", dependsOn: "q9", showIf: "no" }
        ]
    },
    "iii": {
        id: "iii",
        title: "(iii) Loans, investments, etc.",
        questions: [
            { id: "q1", text: "Has the company provided loans or advances in the nature of loans, or stood guarantee, or provided security to any other entity during the year?" },
            { id: "q2", text: "Is the company's principal business to give loans?" },
            { id: "q3", text: "Are the terms and conditions of the grant of all loans and advances in the nature of loans, guarantees, and securities prejudicial to the interest of the company?" },
            { id: "q4", text: "Are the schedule of repayment of principal and payment of interest stipulated?" },
            { id: "q5", text: "Are the repayments of principal and interest regular?", dependsOn: "q4", showIf: "yes" },
            { id: "q6", text: "Are the loans granted repayable on demand?" },
            { id: "q7", text: "If loans or advances were given, have they been repaid on due dates?" },
            { id: "q8", text: "Are there any amounts overdue for >90 days?", dependsOn: "q7", showIf: "no" },
            { id: "q9", text: "Are reasonable steps taken by the company for the recovery of principal and interest?", dependsOn: "q8", showIf: "yes" },
            { id: "q10", text: "Has any loan or advance in nature of loan fallen due during the year?" },
            { id: "q11", text: "Has any loan or advance in nature of loan been renewed or extended or fresh loans granted to settle overdues?", dependsOn: "q10", showIf: "yes" },
            { id: "q12", text: "Has the company granted any loans repayable on demand or without specifying repayment terms?" }
        ]
    },
    "iv": {
        id: "iv",
        title: "(iv) Section 185 and 186",
        questions: [
            { id: "q1", text: "Has the company made any loans, investments, guarantees, or security covered under Section 185 of the Companies Act?" },
            { id: "q2", text: "Has the company complied with the provisions of Section 185?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Has the company made any loans, investments, guarantees, or security covered under Section 186?" },
            { id: "q4", text: "Has the company complied with the provisions of Section 186?", dependsOn: "q3", showIf: "yes" }
        ]
    },
    "v": {
        id: "v",
        title: "(v) Public Deposits",
        questions: [
            { id: "q1", text: "Has the company complied with directives of the Reserve Bank of India?" },
            { id: "q2", text: "Has the company complied with the provisions of sections 73 to 76 of the Companies Act and rules made thereunder?" },
            { id: "q3", text: "Has any order been passed by Company Law Board/NCLT/RBI/any court/tribunal?" },
            { id: "q4", text: "Has the company complied with the same?", dependsOn: "q3", showIf: "yes" }
        ]
    },
    "vi": {
        id: "vi",
        title: "(vi) Cost records",
        questions: [
            { id: "q1", text: "Has the Central Government prescribed maintenance of cost records under section 148(1) of the Companies Act?" },
            { id: "q2", text: "Has the company maintained such accounts and records?", dependsOn: "q1", showIf: "yes" }
        ]
    },
    "vii": {
        id: "vii",
        title: "(vii) Statutory Dues",
        questions: [
            { id: "q1", text: "Is the company regular in depositing undisputed statutory dues (PF, ESI, Income-tax, GST, etc.)?" },
            { id: "q2", text: "Has the company seen slight delays in payment of statutory dues in few cases?", dependsOn: "q1", showIf: "no" },
            { id: "q3", text: "Are there any arrears of outstanding statutory dues as at the last day of the financial year for a period of more than six months?", dependsOn: "q2", showIf: "no" },
            { id: "q4", text: "Are there any statutory dues which have not been deposited on account of any dispute?" }
        ]
    },
    "viii": {
        id: "viii",
        title: "(viii) Undisclosed Income",
        questions: [
            { id: "q1", text: "Has any income been surrendered or disclosed as income during the year in tax assessments under the Income Tax Act?" },
            { id: "q2", text: "Has it been recorded in the books of account?", dependsOn: "q1", showIf: "yes" }
        ]
    },
    "ix": {
        id: "ix",
        title: "(ix) Borrowings",
        questions: [
            { id: "q1", text: "Has the company defaulted in repayment of loans or borrowings or interest thereon to any lender?" },
            { id: "q2", text: "Has the loan rescheduling/restructuring been approved by the concerned lenders during the year?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Does the company have any loans repayable on demand?" },
            { id: "q4", text: "Has the company been declared a wilful defaulter by any bank or financial institution or government or government authority?" },
            { id: "q5", text: "Has the company taken any term loan?" },
            { id: "q6", text: "Were they applied for the purpose for which they were obtained?", dependsOn: "q5", showIf: "yes" },
            { id: "q7", text: "Are there any loans taken near the period-end due to which they remain unutilized?", dependsOn: "q5", showIf: "yes" },
            { id: "q8", text: "Has the company raised funds on short-term basis?" },
            { id: "q9", text: "Have the funds raised on short-term basis been used for long-term purposes?", dependsOn: "q8", showIf: "yes" },
            { id: "q10", text: "Does the company have any subsidiary, associate or joint venture?" },
            { id: "q11", text: "Has the company taken any funds from any entity or person during the year or has any unutilized funds raised through borrowings/issue of securities in previous year?", dependsOn: "q10", showIf: "yes" },
            { id: "q12", text: "Has the company made investment in or given any new loans or advances to its subsidiary, associate or joint venture during the year out of the funds raised or unutilized above?", dependsOn: "q11", showIf: "yes" },
            { id: "q13", text: "Has the company raised loans during the year?" },
            { id: "q14", text: "Have the loans been raised on the pledge of securities held in its subsidiaries, joint ventures or associate companies?", dependsOn: "q13", showIf: "yes" },
            { id: "q15", text: "Has the company defaulted in the repayment of any such loans?", dependsOn: "q14", showIf: "yes" }
        ]
    },
    "x": {
        id: "x",
        title: "(x) Issue of securities",
        questions: [
            { id: "q1", text: "Has the company raised money by way of initial public offer (IPO) or further public offer (FPO) during the year?" },
            { id: "q2", text: "Has the company applied the money for the purposes for which it was raised?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Has the money raised been utilised?", dependsOn: "q2", showIf: "no" },
            { id: "q4", text: "Has the delay/default been rectified by the company subsequently?", dependsOn: "q3", showIf: "yes" },
            { id: "q5", text: "Has the company made any preferential allotment or private placement of shares or convertible debentures during the year?" },
            { id: "q6", text: "Has the company complied with the requirements of section 42 and 62 of the Companies Act?", dependsOn: "q5", showIf: "yes" },
            { id: "q7", text: "Was the money raised applied for the purposes for which it was raised?", dependsOn: "q5", showIf: "yes" }
        ]
    },
    "xi": {
        id: "xi",
        title: "(xi) Fraud",
        forceYes: true,
        questions: [
            { id: "q1", text: "Has any fraud by the company or any fraud on the company been noticed or reported during the year?" },
            { id: "q2", text: "Has any report under section 143(12) of the Companies Act, 2013 been filed with the Central Government in form ADT-4?" },
            { id: "q3", text: "Have whistle blower complaints been received during the year?" }
        ]
    },
    "xii": {
        id: "xii",
        title: "(xii) Nidhi Company",
        questions: [
            { id: "q1", text: "Has the company complied with the Net Owned Funds to Deposits ratio of 1:20?" },
            { id: "q2", text: "Has the company maintained 10% unencumbered term deposits as specified in the Nidhi Rules, 2014?" },
            { id: "q3", text: "Has the company defaulted in payment of interest on deposits or repayment thereof for any period?" }
        ]
    },
    "xiii": {
        id: "xiii",
        title: "(xiii) Related parties",
        questions: [
            { id: "q1", text: "Has the company complied with the provisions of Section 177 and Section 188 of the Companies Act, 2013 and details disclosed as required by applicable accounting standards?" },
            { id: "q2", text: "Is the company a Private company as per section 2(68) of the Companies Act, 2013?" }
        ]
    },
    "xiv": {
        id: "xiv",
        title: "(xiv) Internal Audit",
        questions: [
            { id: "q1", text: "Does the company have an internal audit system commensurate with the size and nature of its business?" },
            { id: "q2", text: "Has the internal audit system been carried out?" }
        ]
    },
    "xv": {
        id: "xv",
        title: "(xv) Non-cash transactions",
        forceYes: true,
        questions: [
            { id: "q1", text: "Has the company entered into any non-cash transactions with directors or persons connected with them?" },
            { id: "q2", text: "Has the approval of members been obtained for entering into the respective transaction?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Has the valuation of asset been obtained from a registered valuer?", dependsOn: "q1", showIf: "yes" }
        ]
    },
    "xvi": {
        id: "xvi",
        title: "(xvi) NBFC",
        questions: [
            { id: "q1", text: "Is the company required to be registered under section 45-IA of the Reserve Bank of India Act, 1934?" },
            { id: "q2", text: "Has the company obtained such registration?", dependsOn: "q1", showIf: "yes" },
            { id: "q3", text: "Has the company conducted any Non-Banking Financial or Housing Finance activities?", dependsOn: "q1", showIf: "yes" },
            { id: "q4", text: "Is the company a Core Investment Company (CIC) as defined in the RBI Regulations?", dependsOn: "q1", showIf: "yes" },
            { id: "q5", text: "Is the company exempt from registration?", dependsOn: "q4", showIf: "yes" },
            { id: "q6", text: "Is the company registered as CIC?", dependsOn: "q5", showIf: "no" },
            { id: "q7", text: "If the company is an exempted or unregistered CIC, has it complied with RBI directions?" },
            { id: "q8", text: "Does the group have any CIC as part of the Group?" },
            { id: "q9", text: "Are the number of CICs in the group more than 1?", dependsOn: "q8", showIf: "yes" }
        ]
    },
    "xvii": {
        id: "xvii",
        title: "(xvii) Cash loss",
        forceYes: true,
        questions: [
            { id: "q1", text: "Has the company incurred cash losses in the financial year?" },
            { id: "q2", text: "Has the company incurred cash losses in the immediately preceding financial year?" }
        ]
    },
    "xviii": {
        id: "xviii",
        title: "(xviii) Resignation of statutory auditors",
        questions: [
            { id: "q1", text: "Has the statutory auditor resigned during the year?" }
        ]
    },
    "xix": {
        id: "xix",
        title: "(xix) Ability to pay liabilities",
        forceYes: true,
        questions: [
            { id: "q1", text: "On the basis of financial ratios, ageing and expected realization of financial assets, payment of financial liabilities, and other information, is there material uncertainty that the company will not be capable of meeting its liabilities existing at the balance sheet date as and when they fall due within a period of one year?" },
            { id: "q2", text: "Is a material uncertainty related to going concern reported?", dependsOn: "q1", showIf: "yes" }
        ]
    },
    "xx": {
        id: "xx",
        title: "(xx) CSR",
        questions: [
            { id: "q1", text: "Has the company spent the required amount under section 135 of the Companies Act on CSR activities?" },
            { id: "q2", text: "Are there any ongoing projects for the unspent CSR amount in the current year?", dependsOn: "q1", showIf: "no" },
            { id: "q3", text: "Is there any unspent CSR amount in respect of other than ongoing projects for the year?", dependsOn: "q1", showIf: "no" },
            { id: "q4", text: "Has the company transferred the amount pertaining to ongoing projects in a special account in compliance with Section 135(6) of the Companies Act, 2013?", dependsOn: "q2", showIf: "yes" },
            { id: "q5", text: "Has the company transferred the amount to a Fund specified in Schedule VII of the Companies Act, 2013 within a period of six months of the expiry of the financial year in compliance with Section 135(5) of the Companies Act, 2013?", dependsOn: "q3", showIf: "yes" }
        ]
    },
    "xxi": {
        id: "xxi",
        title: "(xxi) Component Auditors",
        questions: []
    }
};
